import {
    assignModelOptions,
    BooleanModel,
    BooleanModelOptions,
    ClientManagerInstanceToken,
    ClientManagerInterface,
    CompositeModel,
    ConfigConstants,
    CustomInjector,
    DateModel,
    DisplayDecorator,
    DropdownModel,
    DurationModel,
    ModelOptionsLoaderService,
    ODataObject,
    TextModel,
    TimeModel,
    ValueHelperInstanceToken,
    ValueHelperInterface,
    ValueHelperModel,
    ConfigInstanceToken,
} from '@evorait/pace-core';
import { TranslateService } from '@ngx-translate/core';
import * as _ from 'lodash';
import moment from 'moment';

export default class RequestNotificationModel extends CompositeModel {
    public frontId: TextModel;
    public technicalIdentNo: TextModel;
    public functionalLoc: TextModel;
    public equipmentNo: TextModel;
    public material: TextModel;
    public serialNo: TextModel;
    public issueText: TextModel;
    public frontDate: DateModel;
    public frontTime: TimeModel;
    public startImmediately: DisplayDecorator<BooleanModel>;
    public remoteWork: DisplayDecorator<BooleanModel>;
    public effectCode: DropdownModel<TextModel>;
    public callerName: TextModel;
    public callerPhoneNo: TextModel;
    public jobDuration: DurationModel;
    protected clientManager: ClientManagerInterface;

    public constructor() {
        super('RequestNotificationModel');

        const injector = CustomInjector.getInjector();
        const translate = injector.get(TranslateService);
        const configService = injector.get(ConfigInstanceToken);
        const modelOptionsLoaderService = injector.get(ModelOptionsLoaderService);
        const valueHelperService: ValueHelperInterface = injector.get(ValueHelperInstanceToken);
        this.clientManager = injector.get(ClientManagerInstanceToken);

        this.frontId = <TextModel>this.set(new TextModel('FrontId', configService.getUserFrontId()));
        this.technicalIdentNo = <TextModel>this.set(new TextModel('TechnicalIdentNo'));
        modelOptionsLoaderService.load(this.technicalIdentNo, 'CreateNotification', 'TechnicalIdentNo');

        this.functionalLoc = <TextModel>this.set(new TextModel('FunctionalLoc'));
        modelOptionsLoaderService.load(this.functionalLoc, 'CreateNotification', 'FunctionalLoc');

        this.material = <TextModel>this.set(new TextModel('Material'));
        modelOptionsLoaderService.load(this.material, 'CreateNotification', 'Material');

        this.material.options = {
            ...this.material.options,
            applicationIdentifier: ConfigConstants.ApplicationIdentifier.ProductIdentifier,
        };

        this.serialNo = <TextModel>this.set(new TextModel('SerialNo'));
        modelOptionsLoaderService.load(this.serialNo, 'CreateNotification', 'SerialNo');

        this.serialNo.options = {
            ...this.serialNo.options,
            applicationIdentifier: ConfigConstants.ApplicationIdentifier.SerialNumber,
        };
        this.issueText = <TextModel>this.set(new TextModel('IssueText'));
        modelOptionsLoaderService.load(this.issueText, 'CreateNotification', 'IssueText');

        const t = moment(ConfigConstants.createNotificationDefaultAppTime, ConfigConstants.formatNumberPatternTime);
        const nextDay = t.add(1, 'days');
        this.frontDate = <DateModel>this.set(new DateModel('FrontDate', nextDay));
        modelOptionsLoaderService.load(this.frontDate, 'CreateNotification', 'FrontDate');
        this.frontTime = <TimeModel>this.set(new TimeModel('FrontTime', nextDay));
        modelOptionsLoaderService.load(this.frontTime, 'CreateNotification', 'FrontTime');

        const jobOnFlyDefaultDuration = configService.getJobOnFlyDefaultDuration();
        this.jobDuration = <DurationModel>(
            this.set(new DurationModel('Duration', moment.duration(jobOnFlyDefaultDuration, 'hours')))
        );

        this.jobDuration.options = {
            ...this.jobDuration.options,
            step: 1,
            unit: 'hours',
            minValue: moment.duration(1, 'hours'),
        };

        // suggested appointment date cannot be in past
        const now = moment().startOf('minute');

        this.frontDate.options = {
            ...this.frontDate.options,
            minValue: now,
        };

        this.frontTime.options = {
            ...this.frontTime.options,
            minValue: now,
        };

        if (configService.getShowStartImmediately()) {
            const immediately = new DisplayDecorator(
                <string>translate.instant('DASHBOARD_SIDEMENU_CREATE_NOTIFICATION_START_IMMEDIATELY'),
                new BooleanModel('StartImmediately', false),
            );
            this.startImmediately = <DisplayDecorator<BooleanModel>>this.set(immediately);
            modelOptionsLoaderService.load(this.startImmediately, 'CreateNotification', 'StartImmediately');

            // Only show Remote Work checkbox if both Start Immediately and Remote Work are enabled in config
            if (configService.getShowRemoteWork()) {
                this.remoteWork = new DisplayDecorator(
                    <string>translate.instant('DASHBOARD_SIDEMENU_CREATE_NOTIFICATION_REMOTE_WORK'),
                    new BooleanModel('RemoteWork', false, new BooleanModelOptions({ disabled: true })),
                );
                modelOptionsLoaderService.load(this.remoteWork, 'CreateNotification', 'RemoteWork');
            }
        }

        const options = valueHelperService.getNotificationEffectCode().map((vh: ValueHelperModel) => {
            return new DisplayDecorator<TextModel>(vh.descText, new TextModel(vh.descText, vh.code));
        });

        this.effectCode = <DropdownModel<TextModel>>this.set(new DropdownModel<TextModel>('EffectCode', options));
        modelOptionsLoaderService.load(this.effectCode, 'CreateNotification', 'EffectCode');

        this.callerName = <TextModel>this.set(new TextModel('CallerName'));
        modelOptionsLoaderService.load(this.callerName, 'CreateNotification', 'CallerName');

        this.callerPhoneNo = <TextModel>this.set(new TextModel('CallerPhoneNo'));
        modelOptionsLoaderService.load(this.callerPhoneNo, 'CreateNotification', 'CallerPhoneNo');
    }

    /**
     * Called from markup to reset required and readonly properties of input fields based on the logic that
     * only "Technical Id" or "Functional location" or "Material number" plus "Serial number" can be filled out.
     */
    public resetModelOptions(): void {
        const functionalLocRequired =
            !this.equipmentNo?.value && !this.technicalIdentNo?.value && !this.material.value && !this.serialNo.value;
        const materialRequired = !this.equipmentNo?.value && !this.technicalIdentNo?.value && !this.functionalLoc.value;
        const serialNoRequired = !this.equipmentNo?.value && !this.technicalIdentNo?.value && !this.functionalLoc.value;

        if (typeof this.technicalIdentNo !== 'undefined') {
            const technicalIdentNoRequired = !this.functionalLoc.value && !this.material.value && !this.serialNo.value;
            this.technicalIdentNo = assignModelOptions(
                this.technicalIdentNo,
                {
                    ...this.technicalIdentNo.options,
                    required: technicalIdentNoRequired,
                    readonly: !technicalIdentNoRequired,
                },
                this,
            );
        }
        if (typeof this.equipmentNo !== 'undefined') {
            const equipmentNoRequired = !this.functionalLoc.value && !this.material.value && !this.serialNo.value;
            this.equipmentNo = assignModelOptions(
                this.equipmentNo,
                {
                    ...this.equipmentNo.options,
                    required: equipmentNoRequired,
                    readonly: !equipmentNoRequired,
                },
                this,
            );
        }

        // set readonly and required options
        this.functionalLoc = assignModelOptions(
            this.functionalLoc,
            {
                ...this.functionalLoc.options,
                required: functionalLocRequired,
                readonly: !functionalLocRequired,
            },
            this,
        );
        this.material = assignModelOptions(
            this.material,
            {
                ...this.material.options,
                required: materialRequired,
                readonly: !materialRequired,
            },
            this,
        );
        this.serialNo = assignModelOptions(
            this.serialNo,
            {
                ...this.serialNo.options,
                required: serialNoRequired,
                readonly: !serialNoRequired,
            },
            this,
        );
    }

    /**
     * Called from markup to reset suggested appointment date and time
     * If "start immediately" checkbox is checked, it should display current date and time and cannot be edited
     * If unchecked, then display default date and time and can be changed by user
     */
    public onChangeStartImmediately(): void {
        if (this.startImmediately.model.value) {
            const now = moment().startOf('minute');
            this.frontDate.value = now;
            this.frontTime.value = now;
        } else {
            const t = moment(ConfigConstants.createNotificationDefaultAppTime, ConfigConstants.formatNumberPatternTime);
            const nextDay = t.add(1, 'days');
            this.frontDate.value = nextDay;
            this.frontTime.value = nextDay;
            if (this.remoteWork?.value) {
                this.remoteWork.model.value = false;
            }
        }
        // We are creating here a new reference for the existing object to enforce
        // the change detection to triggering. This is necessary because of OnChanges can recognize the

        // new instance of the object. Without that, we have to implement DoCheck and OnChanges at the same time
        // in the date- and time-input-component and this is not recommend (eslint/angular) and can lead to errors.
        this.frontDate = _.cloneDeep(this.frontDate);
        this.frontTime = _.cloneDeep(this.frontTime);

        this.frontDate = assignModelOptions(
            this.frontDate,
            {
                ...this.frontDate.options,
                readonly: this.startImmediately.model.value,
            },
            this,
        );
        this.frontTime = assignModelOptions(
            this.frontTime,
            {
                ...this.frontTime.options,
                readonly: this.startImmediately.model.value,
            },
            this,
        );
        if (this.remoteWork) {
            this.remoteWork = assignModelOptions(
                this.remoteWork,
                {
                    ...this.remoteWork.options,
                    disabled: !this.startImmediately.model.value,
                },
                this,
            );
        }
    }

    public getOutModel(): ODataRequestNotificationOutModel {
        // create outmodel
        const outModel = <ODataRequestNotificationOutModel>super.getOutModel();
        if (outModel.TechnicalIdentNo) {
            outModel.TechnicalIdentNo = outModel.TechnicalIdentNo.toUpperCase();
        }
        if (this.jobDuration.value) {
            outModel.Duration = this.jobDuration.value.asHours();
        }
        if (this.remoteWork?.value) {
            outModel.StartImmediately = 'R';
        }
        return outModel;
    }
}

export interface ODataRequestNotificationOutModel extends ODataObject {
    FrontId: string;
    TechnicalIdentNo: string;
    EquipmentNum: string;
    FunctionalLoc: string;
    Material: string;
    SerialNo: string;
    IssueText: string;
    FrontDate: string;
    FrontTime: string;
    Duration: number;
    StartImmediately: string;
    EffectCode: string;
    CallerName: string;
    CallerPhoneNo: string;
    SalesOrg: string;
    Plant: string;
}
